import { useCallback } from 'react';
import useCommonImageUpload from '@/ueshook/batchToolsHook/useImageUpload/useCommonImageUpload';
import useCommonImageUploader from '@/ueshook/batchToolsHook/useImageUpload/useCommonImageUploader';
import { useImageProcessing } from '../context/ImageProcessingContext';
import type { ImageStatus } from '../context/ImageProcessingContext';

interface UseImageUploaderProps {
  from?: string;
}

/**
 * 水印移除专用图像上传Hook
 * @description 组合公共Hook和业务特有Hook
 */
export function useImageUploader(props: UseImageUploaderProps = {}) {
  const { from = 'ImageUploader' } = props;
  const { ImageState, dispatchImage } = useImageProcessing();

  // 创建水印移除专用的初始状态工厂函数
  const createInitialImageStatus = (
    file: File,
    imageId: string
  ): ImageStatus => {
    return {
      localImgId: imageId,
      localImgName: file.name,
      localImgType: file.type,
      localImgSize: { width: 0, height: 0 },

      generateStatus: 'generating',
      generateStatusText: '',
      localImgUrl: '',
      localFile: file,

      uploadStatus: 'ready',
      uploadStatusText: '',
      uploadUrl: null,
      uploadId: null,

      exampleSmearUrl: null,
      exampleSmearFile: null,
      cacheLines: null,
      smearSaveStatus: 'ready',
      smearSaveStatusText: null,
      uploadSmearStatus: 'ready',
      uploadSmearStatusText: null,
      uploadSmearFile: null,
      uploadSmearUrl: null,
      uploadSmearId: null,

      imgQuality: 'pro',
      rmwStatus: 'ready',
      batchTaskId: null,
      outputImageUrl: null,
      outputThumbnail: null,
      outputError: null,
      outputRate: null,

      showImgState: 'Original',
    };
  };

  // 使用公共的图像上传Hook
  const { uploadOriginalImages, handleImageUploadInit } =
    useCommonImageUploader({
      from: `image_rmw_${from}`,
      ImageState,
      dispatchImage,
      createInitialImageStatus,
    });

  // 使用业务特有的涂鸦图像上传Hook
  const { uploadSmearImages } = useSmearImageUploader({
    ImageState,
    dispatchImage,
  });

  /**
   * 检查并上传所有需要上传的图像
   */
  const checkAndUploadImages = useCallback(
    async (_form?: string) => {
      await uploadOriginalImages();
      await uploadSmearImages();
    },
    [uploadOriginalImages, uploadSmearImages]
  );

  return { uploadSmearImages, handleImageUploadInit, checkAndUploadImages };
}

export default useImageUploader;

/**
 * 涂鸦图像上传Hook - watermark-removal业务特有
 * @description 复用公共上传逻辑，专门处理涂鸦图像的上传功能
 */
interface UseSmearImageUploaderProps {
  ImageState: { images: Map<string, ImageStatus> };
  dispatchImage: React.Dispatch<any>;
}

function useSmearImageUploader(props: UseSmearImageUploaderProps) {
  const { ImageState, dispatchImage } = props;

  // 复用公共的图像上传Hook
  const { uploadImages } = useCommonImageUpload();

  /**
   * 上传涂鸦图像
   * @param specificImage 可选的特定图像，如果提供则只上传该图像
   */
  const uploadSmearImages = useCallback(
    async (specificImage?: ImageStatus) => {
      try {
        // 筛选需要上传的涂鸦图像
        const smearImages = specificImage
          ? [specificImage].filter(
              (status) =>
                !status.uploadSmearUrl &&
                !status.uploadSmearId &&
                status.uploadSmearFile
            )
          : Array.from(ImageState.images.values()).filter((status) => {
              return (
                status.smearSaveStatus === 'success' &&
                !status.uploadSmearUrl &&
                !status.uploadSmearId &&
                status.uploadSmearFile
              );
            });

        if (smearImages.length === 0) return;

        // console.log(
        //   `开始上传${smearImages.length}张涂鸦图像:`,
        //   timestampToFormattedTime(),
        //   smearImages
        // );

        // 转换为适合上传的格式（将涂鸦文件作为主文件）
        const uploadableImages = smearImages.map((img) => ({
          ...img,
          localFile: img.uploadSmearFile!, // 使用涂鸦文件作为上传文件
        }));

        // 复用公共上传逻辑
        await uploadImages(
          uploadableImages,
          'smear_upload',
          // 进度回调 - 更新涂鸦上传状态
          (imageId, status, message) => {
            dispatchImage({
              type: 'UPDATE_IMAGE',
              payload: {
                id: imageId,
                status: {
                  uploadSmearStatus: status,
                  uploadSmearStatusText: message,
                },
              },
            });
          },
          // 完成回调 - 更新涂鸦上传结果
          (imageId, result) => {
            if (result.success) {
              dispatchImage({
                type: 'UPDATE_IMAGE',
                payload: {
                  id: imageId,
                  status: {
                    uploadSmearStatus: 'success',
                    uploadSmearStatusText: 'upload smear success',
                    uploadSmearUrl: result.url,
                    uploadSmearId: result.fileId,
                  },
                },
              });
            } else {
              dispatchImage({
                type: 'UPDATE_IMAGE',
                payload: {
                  id: imageId,
                  status: {
                    uploadSmearStatus: 'failed',
                    uploadSmearStatusText: `upload smear failed: ${result.error}`,
                  },
                },
              });
            }
          }
        );
      } catch (error) {
        console.error('上传涂鸦图像出错:', error);
      }
    },
    [ImageState.images, dispatchImage, uploadImages]
  );

  return { uploadSmearImages };
}
